import type { Encounter } from '@medplum/fhirtypes';

import { Grid, Stack, Text } from '@eluve/components';

import { FhirStatusTag } from '../../components/FhirStatusTag';
import type { FhirView } from '../../core/view-contract';
import { formatFhirDate } from '../observation/format';

/**
 * The exported Encounter component. Visit-history lists are the primary
 * surface. Participants are BackboneElements (not top-level references), so
 * the view reads display strings directly, like AppointmentView does.
 */
export const EncounterView: FhirView<Encounter> = (props) => {
  if (props.ctx.inList) return <EncounterRowView {...props} />;
  return <EncounterReadView {...props} />;
};

function encounterName(data: Encounter): string {
  return (
    data.type?.[0]?.text ??
    data.type?.[0]?.coding?.[0]?.display ??
    data.class?.display ??
    'Encounter'
  );
}

function providerText(data: Encounter): string | undefined {
  const names = (data.participant ?? [])
    .map((participant) => participant.individual?.display)
    .filter(Boolean);
  return names.length > 0 ? names.join(', ') : undefined;
}

function reasonText(data: Encounter): string | undefined {
  return (
    data.reasonCode?.[0]?.text ?? data.reasonCode?.[0]?.coding?.[0]?.display
  );
}

/** Visit-history row: type, date, provider, reason. */
const EncounterRowView: FhirView<Encounter> = ({ data }) => {
  const date = formatFhirDate(data.period?.start);
  const provider = providerText(data);
  const reason = reasonText(data);
  return (
    <Grid
      data-view="encounter-row"
      align="center"
      className="grid-cols-[minmax(0,1fr)_auto] gap-x-4 py-2"
    >
      <div className="min-w-0">
        <Stack
          direction="horizontal"
          gap={2}
          align="center"
          className="min-w-0"
        >
          <Text inline size="sm" weight="regular" className="truncate">
            {encounterName(data)}
          </Text>
          {data.status !== 'finished' && <FhirStatusTag status={data.status} />}
        </Stack>
        {(reason ?? provider) && (
          <Text size="xs" color="tertiary" className="truncate">
            {[reason, provider].filter(Boolean).join(' · ')}
          </Text>
        )}
      </div>
      {date && (
        <Text inline size="xs" color="tertiary">
          {date}
        </Text>
      )}
    </Grid>
  );
};

/** Standalone card. */
const EncounterReadView: FhirView<Encounter> = ({ data, ctx }) => {
  const date = formatFhirDate(data.period?.start);
  const provider = providerText(data);
  const reason = reasonText(data);
  return (
    <div
      data-view="encounter-read"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold" className="truncate">
          {encounterName(data)}
        </Text>
        <FhirStatusTag status={data.status} />
      </Stack>
      <Stack
        direction="horizontal"
        wrap
        align="stretch"
        className="mt-1 gap-x-4"
      >
        {date && (
          <Text inline size="xs" color="tertiary">
            {date}
          </Text>
        )}
        {provider && (
          <Text inline size="xs" color="tertiary">
            {provider}
          </Text>
        )}
      </Stack>
      {reason && (
        <Text size="sm" weight="regular" className="mt-1">
          {reason}
        </Text>
      )}
      {ctx.debug && (
        <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>
      )}
    </div>
  );
};
